import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Briefcase, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import WorkflowEngagementModal from "@/components/admin/WorkflowEngagementModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge colors for engagement status
const engagementStatusColors = {
  DRAFT: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  ACTIVE: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  PAUSED: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  COMPLETED: "bg-green-500/20 text-green-400 border-green-500/30",
  CANCELED: "bg-red-500/20 text-red-400 border-red-500/30"
};

// Payment status badge colors
const paymentStatusColors = {
  PENDING: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  SUCCEEDED: "bg-green-500/20 text-green-400 border-green-500/30",
  FAILED: "bg-red-500/20 text-red-400 border-red-500/30",
  REFUNDED: "bg-slate-500/20 text-slate-400 border-slate-500/30"
};

const AdminEngagements = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [selectedSerId, setSelectedSerId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchEngagements = async () => {
    setIsLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (paymentFilter !== "all") params.append("payment_status", paymentFilter);
      
      const response = await axios.get(`${API}/workflow/admin/engagements?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEngagements(response.data.engagements || []);
    } catch (error) {
      console.error("Failed to fetch engagements:", error);
      toast.error("Failed to fetch engagements");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEngagements();
  }, [token, statusFilter, paymentFilter]);

  const handleOpenDetail = (serId) => {
    setSelectedSerId(serId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSerId(null);
    // Refresh list after modal closes in case changes were made
    fetchEngagements();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-engagements">
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <Briefcase className="w-5 h-5 text-primary" />
              Workflow Engagements ({engagements.length})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEngagements}
              className="text-slate-400"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Filters */}
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Status:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32 bg-navy-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  <SelectItem value="all" className="text-white">All</SelectItem>
                  <SelectItem value="ACTIVE" className="text-white">Active</SelectItem>
                  <SelectItem value="COMPLETED" className="text-white">Completed</SelectItem>
                  <SelectItem value="PAUSED" className="text-white">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Payment:</span>
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="w-32 bg-navy-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  <SelectItem value="all" className="text-white">All</SelectItem>
                  <SelectItem value="SUCCEEDED" className="text-white">Paid</SelectItem>
                  <SelectItem value="PENDING" className="text-white">Pending</SelectItem>
                  <SelectItem value="FAILED" className="text-white">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {engagements.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Client Email</TableHead>
                    <TableHead className="text-slate-400">Service</TableHead>
                    <TableHead className="text-slate-400">Payment</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Current Step</TableHead>
                    <TableHead className="text-slate-400">Next Action</TableHead>
                    <TableHead className="text-slate-400">Updated</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {engagements.map((eng) => (
                    <TableRow
                      key={eng.ser_id}
                      className="border-slate-800 hover:bg-navy-800/50 cursor-pointer"
                      onClick={() => handleOpenDetail(eng.ser_id)}
                      data-testid={`engagement-row-${eng.ser_id}`}
                    >
                      <TableCell className="text-white font-medium">
                        {eng.user_email}
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-300 text-sm" title={eng.service_public_id}>
                          {eng.service_name || eng.service_public_id}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={paymentStatusColors[eng.payment_status] || paymentStatusColors.PENDING}>
                          {eng.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={engagementStatusColors[eng.engagement_status] || engagementStatusColors.ACTIVE}>
                          {eng.engagement_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm max-w-[150px] truncate">
                        {eng.current_step?.replace(/_/g, ' ') || '-'}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm max-w-[180px] truncate">
                        {eng.next_action_label || '-'}
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {eng.updated_at ? new Date(eng.updated_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetail(eng.ser_id);
                          }}
                          data-testid={`open-engagement-${eng.ser_id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Briefcase className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-2">No engagements found</p>
              <p className="text-slate-500 text-sm">
                Engagements will appear here once clients complete their payment.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Workflow Engagement Operations Modal */}
      <WorkflowEngagementModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        serId={selectedSerId}
      />
    </div>
  );
};

export default AdminEngagements;
